import { Component, ChangeDetectionStrategy, ChangeDetectorRef, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { take } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Client } from 'src/providers/client/client';

import { PropertyTaxDetailsPage } from 'src/pages/finance/property-tax-details/property-tax-details';

import { MatTooltipModule } from '@angular/material/tooltip';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

import {
  ACTIVE_TAX_STATUSES,
  AddressTax,
  StatusTone,
  TONE_BG,
  TONE_COLOR,
  TaxesService,
  formatMonthDay,
  isTaxOverdue,
  translateTax,
} from 'src/shared/services/taxes.service';

interface PropertySummaryVM {
  id: number;
  name: string;
  color: string;
  taxCountLine: string;
  nextUpLine: string;
  nextUpOverdue: boolean;
  statusLine: string;
  statusTone: StatusTone;
  activeCount: number;
  needAttentionCount: number;
  overdueCount: number;
}

interface TaxesSummary {
  properties: number;
  taxesTracked: number;
  needAttention: number;
  overdue: number;
}

@Component({
  selector: 'taxes-content',
  templateUrl: './taxes-content.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    MatTooltipModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TaxesContentComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private client = inject(Client);
  private taxesService = inject(TaxesService);
  readonly windowService = inject(WindowService);

  loaded = false;
  errorMessage = '';
  properties: PropertySummaryVM[] = [];
  summary: TaxesSummary = { properties: 0, taxesTracked: 0, needAttention: 0, overdue: 0 };
  headers: string[] = ['Property', 'Taxes', 'Status', 'Next Up', 'last-column'];
  rows: any[] = [];
  readonly toneColor = TONE_COLOR;
  readonly toneBg = TONE_BG;

  async ngOnInit() {
    this.storage.save('dialog-right-side-open', false);
    await this.loadProperties();
  }

  async loadProperties() {
    try {
      // Without address_ids the API answers the taxes of every property the user can see.
      const [addresses, taxes] = await Promise.all([
        this.client.getAddresses(),
        this.taxesService.getAddressTaxes(),
      ]);
      const taxesByAddress = new Map<number, AddressTax[]>();
      (taxes || []).forEach(tax => {
        taxesByAddress.set(tax.address_id, [...(taxesByAddress.get(tax.address_id) || []), tax]);
      });
      this.properties = (addresses || []).map(address => this.buildSummaryVM(address, taxesByAddress.get(address.id) || []));
      this.summary = {
        properties: this.properties.length,
        taxesTracked: this.properties.reduce((sum, property) => sum + property.activeCount, 0),
        needAttention: this.properties.reduce((sum, property) => sum + property.needAttentionCount, 0),
        overdue: this.properties.reduce((sum, property) => sum + property.overdueCount, 0),
      };
      this.rows = this.properties.map(property => ({
        id: property.id,
        'Property': { value: property.name, action: null, color: property.color },
        'Taxes': { value: property.taxCountLine, action: null },
        'Status': { value: property.statusLine, action: null, icon: `assets/svg/status-dot-${property.statusTone}.svg` },
        'Next Up': { value: `${property.nextUpLine}`, action: null },
        'last-column': { value: property.id, action: null, hasChevron: true },
      }));
    } catch (error) {
      this.errorMessage = error?.error?.message || error?.message || 'Failed to load taxes';
    }
    this.loaded = true;
    this.cdr.markForCheck();
  }

  private buildSummaryVM(address: any, taxes: AddressTax[]): PropertySummaryVM {
    const active = taxes.filter(tax => ACTIVE_TAX_STATUSES.includes(tax.status));
    const suggestedCount = taxes.filter(tax => tax.status === 'suggested').length;
    const overdueCount = active.filter(isTaxOverdue).length;
    const actionCount = active.filter(tax => tax.status === 'action_needed').length;
    const pendingCount = active.filter(tax => tax.status === 'pending').length;

    let statusLine = translateTax('All registered');
    let statusTone: StatusTone = 'good';
    if (active.length === 0) {
      statusLine = translateTax(suggestedCount > 0 ? 'Review suggestions' : 'No taxes yet');
      statusTone = 'warn';
    } else if (overdueCount > 0) {
      statusLine = translateTax('%count% overdue', { count: overdueCount });
      statusTone = 'bad';
    } else if (actionCount > 0) {
      statusLine = translateTax('Action needed');
      statusTone = 'warn';
    } else if (pendingCount > 0) {
      statusLine = translateTax('Registration needed');
      statusTone = 'warn';
    } else if (suggestedCount > 0) {
      statusLine = translateTax('Review suggestions');
      statusTone = 'warn';
    }

    const nextUp = this.buildNextUp(active, suggestedCount);

    return {
      id: address.id,
      name: this.addressName(address),
      color: address.color || '#72CB9B',
      taxCountLine: [
        active.length > 0
          ? translateTax(active.length === 1 ? '%count% tax' : '%count% taxes', { count: active.length })
          : translateTax('None yet'),
        suggestedCount > 0 ? translateTax('%count% suggested', { count: suggestedCount }) : '',
      ].filter(Boolean).join(' · '),
      nextUpLine: nextUp.line,
      nextUpOverdue: nextUp.overdue,
      statusLine,
      statusTone,
      activeCount: active.length,
      needAttentionCount: actionCount + pendingCount,
      overdueCount,
    };
  }

  // The soonest unfiled filing across the property's adopted taxes.
  private buildNextUp(active: AddressTax[], suggestedCount: number): { line: string; overdue: boolean } {
    const withFilings = active
      .filter(tax => tax.next_filing)
      .sort((a, b) => a.next_filing.due_date.localeCompare(b.next_filing.due_date));
    if (withFilings.length === 0) {
      if (active.length > 0) { return { line: translateTax('No upcoming filings'), overdue: false }; }
      return { line: translateTax(suggestedCount > 0 ? 'Review suggested taxes' : 'No taxes yet'), overdue: false };
    }
    const next = withFilings[0];
    const overdue = next.next_filing.status === 'overdue';
    return {
      line: `${formatMonthDay(next.next_filing.due_date)} — ${next.name}${overdue ? ` (${translateTax('overdue')})` : ''}`,
      overdue,
    };
  }

  private addressName(address: any): string {
    if (address.address_name) { return address.address_name; }
    const line = [address.address1, address.address2].filter(Boolean).join(' ');
    return [line, address.city, address.zip].filter(Boolean).join(', ');
  }

  onTableColumnClick(event: any): void {
    const property = this.properties.find(item => item.id === event.element.id);
    if (!property) { return; }
    this.openPropertyDetails(property);
  }

  async openPropertyDetails(property: PropertySummaryVM): Promise<void> {
    const params = { propertyId: property.id, propertyName: property.name, propertyColor: property.color };
    if (this.windowService.isDesktopRes) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo('property-tax-details', params, PropertyTaxDetailsPage);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(PropertyTaxDetailsPage);
      }
      this.rightSidePanelService.panelClosed().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(async () => {
        if (await this.storage.retrieve('didMakePropertyTaxChanges')) {
          await this.storage.save('didMakePropertyTaxChanges', false);
          await this.loadProperties();
        }
      });
    } else {
      this.navCtrl.navigateForward('property-tax-details', params);
    }
  }
}
